import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	TemplateRef,
	ViewChild,
	computed,
	effect,
	forwardRef,
	inject,
	input,
	model,
	output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { MatOption } from '@angular/material/core';
import {
	MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
	MatAutocomplete,
	MatAutocompleteModule,
	MatAutocompleteSelectedEvent,
	MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { startWith } from 'rxjs';

export type SelectValue = string | number;

export interface SelectOption<V extends SelectValue = string> {
	id: V;
	title: string;
	disabled?: boolean;
}

export function selectScrollStrategyFactory(overlay: Overlay): () => ScrollStrategy {
	return () => overlay.scrollStrategies.close();
}

@Component({
	selector: 'app-select',
	standalone: true,
	imports: [MatAutocompleteModule, NgTemplateOutlet, NgClass],
	templateUrl: './select.component.html',
	styleUrl: './select.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true },
		{ provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY, useFactory: selectScrollStrategyFactory, deps: [Overlay] },
	],
})
export class SelectComponent<V extends SelectValue = string> implements ControlValueAccessor, AfterViewInit {
	@ViewChild('inputEl') private inputRef?: ElementRef<HTMLInputElement>;
	@ViewChild(MatAutocompleteTrigger) protected trigger?: MatAutocompleteTrigger;
	@ViewChild(MatAutocomplete) private autocomplete?: MatAutocomplete;

	private readonly destroyRef = inject(DestroyRef);

	options = input.required<SelectOption<V>[]>();
	value = model<SelectOption<V> | null>(null);

	placeholder = input<string>('');
	searchable = input<boolean>(false);
	filterLocally = input<boolean>(true);
	hasSearchIcon = input<boolean>(false);
	disabled = input<boolean>(false);
	loading = input<boolean>(false);
	clearable = input<boolean>(false);
	invalid = input<boolean>(false);
	clearOnMissing = input<boolean>(false);
	panelClass = input<string>('');
	ariaLabelledby = input<string>('');
	optionTemplate = input<TemplateRef<{ $implicit: SelectOption<V> }>>();

	selectionChange = output<SelectOption<V> | null>();
	searchTextChange = output<string>();

	/** What the user is typing. `null` means "not editing", so the title is shown. */
	private readonly searchText = signal<string | null>(null);
	private readonly formDisabled = signal(false);

	protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

	protected readonly selectedOption = computed<SelectOption<V> | null>(() => {
		const value = this.value();
		if (value === null) {
			return null;
		}

		return this.options().find((option) => option.id === value.id) ?? value;
	});

	protected readonly displayText = computed(() => this.searchText() ?? this.selectedOption()?.title ?? '');

	protected readonly filteredOptions = computed(() => {
		const term = this.searchText();
		if (!this.filterLocally() || term === null || term === '') {
			return this.options();
		}

		const needle = term.trim().toLowerCase();
		return this.options().filter((option) => option.title.toLowerCase().includes(needle));
	});

	protected readonly showClear = computed(() => this.clearable() && this.value() !== null && !this.isDisabled());

	private onChange: (selectOption: SelectOption<V> | null) => void = () => {};
	private onTouched: () => void = () => {};

	constructor() {
		effect(
			() => {
				if (!this.clearOnMissing() || this.value() === null) {
					return;
				}

				const options = this.options();
				if (!options.length || options.some((option) => option.id === this.value()?.id)) {
					return;
				}

				this.commit(null);
			},
			{ allowSignalWrites: true },
		);

		effect(() => {
			this.selectedOption();
			queueMicrotask(() => this.syncPanelSelection());
		});
	}

	ngAfterViewInit(): void {
		this.autocomplete?.options.changes
			.pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.syncPanelSelection());
	}

	private syncPanelSelection(): void {
		const options = this.autocomplete?.options;
		if (!options?.length) {
			return;
		}

		const selectedId = this.selectedOption()?.id ?? null;
		options.forEach((option: MatOption) => {
			const optionId = (option.value as SelectOption<V> | null)?.id ?? null;
			const isCurrent = selectedId !== null && optionId === selectedId;

			if (isCurrent && !option.selected) {
				option.select(false);
			} else if (!isCurrent && option.selected) {
				option.deselect(false);
			}
		});
	}

	writeValue(value: SelectOption<V> | null): void {
		this.value.set(value ?? null);
		this.searchText.set(null);
	}

	registerOnChange(fn: (selectOption: SelectOption<V> | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.formDisabled.set(isDisabled);
	}

	protected readonly displayFn = (option: SelectOption<V> | null): string => option?.title ?? '';

	protected onFocus(): void {
		if (!this.searchable()) {
			this.trigger?.openPanel();
			return;
		}

		this.inputRef?.nativeElement.select();
	}

	protected onClick(): void {
		if (!this.searchable()) {
			this.trigger?.openPanel();
		}
	}

	protected onInput(event: Event): void {
		const text = (event.target as HTMLInputElement).value;

		this.searchText.set(text);
		this.searchTextChange.emit(text);

		if (text === '' && this.clearable()) {
			this.commit(null);
		}
	}

	protected onOptionSelected(event: MatAutocompleteSelectedEvent): void {
		this.searchText.set(null);
		this.commit(event.option.value as SelectOption<V>);
		this.onTouched();
	}

	protected onKeydown(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			this.searchText.set(null);
		}
	}

	protected onBlur(): void {
		this.onTouched();

		const text = this.searchText();
		if (text === null) {
			return;
		}

		this.searchText.set(null);

		if (text.trim() === '') {
			if (this.clearable()) {
				this.commit(null);
			}
			return;
		}

		const match = this.findExactMatch(text);
		if (match) {
			this.commit(match);
		}
	}

	protected onClear(event: MouseEvent): void {
		event.stopPropagation();

		this.searchText.set(null);
		this.commit(null);
		this.onTouched();
		this.inputRef?.nativeElement.focus();
	}

	private commit(option: SelectOption<V> | null): void {
		this.value.set(option ?? null);
		this.onChange(option ?? null);
		this.selectionChange.emit(option);
	}

	private findExactMatch(text: string): SelectOption<V> | null {
		const needle = text.trim().toLowerCase();
		return this.options().find((option) => !option.disabled && option.title.trim().toLowerCase() === needle) ?? null;
	}
}
